import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAccessToken } from '../useProfile';

type PlannedWorkout = {
  id: number;
  [key: string]: unknown;
};

type LinkPlannedWorkoutVariables = {
  workout: PlannedWorkout;
  completedWorkoutId: number;
};

type UnlinkPlannedWorkoutVariables = {
  workout: PlannedWorkout;
};

const plannedWorkoutLinkQueryKeys = [
  'racePlan',
  'plannedWorkouts',
  'plannedWorkout',
  'workout',
];

export const useLinkPlannedWorkout = ({
  onSuccess,
}: {
  onSuccess?: () => void;
} = {}) => {
  const { data: accessToken } = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workout,
      completedWorkoutId,
    }: LinkPlannedWorkoutVariables) => {
      const response = await fetch(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/profile/plan/workouts/${workout.id}/update`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            id: workout.id,
            activity_id: completedWorkoutId,
            completed: true,
          }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to link planned workout');
      }

      return response.text();
    },
    onSuccess: async () => {
      await Promise.all(
        plannedWorkoutLinkQueryKeys.map((queryKey) =>
          queryClient.invalidateQueries({ queryKey: [queryKey] }),
        ),
      );
      onSuccess?.();
    },
  });
};

export const useUnlinkPlannedWorkout = () => {
  const { data: accessToken } = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ workout }: UnlinkPlannedWorkoutVariables) => {
      const response = await fetch(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/profile/plan/workouts/${workout.id}/update`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            id: workout.id,
            activity_id: null,
            completed: false,
          }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to unlink planned workout');
      }

      return response.text();
    },
    onSuccess: async () => {
      await Promise.all(
        plannedWorkoutLinkQueryKeys.map((queryKey) =>
          queryClient.invalidateQueries({ queryKey: [queryKey] }),
        ),
      );
    },
  });
};
